import { Entity } from './Entity';
import type { Component, ComponentClass } from './Component';
import { System } from './System';
import { EventBus, GameEvents } from '@/core/EventBus';
import { Owner } from './components/UnitComponents';

/**
 * World - container for entities and systems
 */
export class World {
  private entities: Map<string, Entity> = new Map();
  private systems: System[] = [];
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  /**
   * Create and add a new entity
   */
  createEntity(id?: string): Entity {
    const entity = new Entity(id);
    this.entities.set(entity.id, entity);
    this.eventBus.emit(GameEvents.ENTITY_SPAWNED, { entityId: entity.id });
    return entity;
  }

  /**
   * Add an existing entity to the world
   */
  addEntity(entity: Entity): void {
    this.entities.set(entity.id, entity);
    this.eventBus.emit(GameEvents.ENTITY_SPAWNED, { entityId: entity.id });
  }

  /**
   * Remove an entity from the world
   */
  removeEntity(entityOrId: Entity | string): void {
    const id = typeof entityOrId === 'string' ? entityOrId : entityOrId.id;
    const entity = this.entities.get(id);

    if (entity) {
      entity.destroy();
      this.entities.delete(id);
      this.eventBus.emit(GameEvents.ENTITY_DESTROYED, { entityId: id });
    }
  }

  /**
   * Get an entity by ID
   */
  getEntity(id: string): Entity | undefined {
    return this.entities.get(id);
  }

  /**
   * Query entities with specific components
   */
  query<T extends ComponentClass[]>(
    ...componentClasses: T
  ): Entity[] {
    return Array.from(this.entities.values()).filter(
      (entity) => !entity.destroyed && entity.hasComponents(componentClasses)
    );
  }

  /**
   * Query for single entity with components (returns first match)
   */
  queryOne<T extends ComponentClass[]>(
    ...componentClasses: T
  ): Entity | undefined {
    for (const entity of this.entities.values()) {
      if (!entity.destroyed && entity.hasComponents(componentClasses)) {
        return entity;
      }
    }
    return undefined;
  }

  /**
   * Get entities with a specific component and value
   */
  queryByComponent<T extends Component>(
    componentClass: ComponentClass<T>,
    predicate: (component: T) => boolean
  ): Entity[] {
    return this.query(componentClass).filter((entity) => {
      const component = entity.getComponent(componentClass);
      return component && predicate(component);
    });
  }

  /**
   * Add a system to the world
   */
  addSystem(system: System): void {
    system.init(this);
    this.systems.push(system);
    // Sort by priority (lower priority runs first)
    this.systems.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Remove a system from the world
   */
  removeSystem(system: System): void {
    const index = this.systems.indexOf(system);
    if (index !== -1) {
      system.dispose();
      this.systems.splice(index, 1);
    }
  }

  /**
   * Update all systems
   */
  update(deltaTime: number): void {
    for (const system of this.systems) {
      if (system.enabled) {
        system.update(deltaTime);
      }
    }
    this.cleanupDestroyedEntities();
  }

  /**
   * Render all systems
   */
  render(interpolation: number): void {
    for (const system of this.systems) {
      if (system.enabled) {
        system.render(interpolation);
      }
    }
  }

  /**
   * Remove destroyed entities
   */
  private cleanupDestroyedEntities(): void {
    for (const [id, entity] of this.entities) {
      if (entity.destroyed) {
        this.entities.delete(id);
      }
    }
  }

  /**
   * Get all entities
   */
  getAllEntities(): Entity[] {
    return Array.from(this.entities.values()).filter((e) => !e.destroyed);
  }

  /**
   * Get entities by owner playerId
   */
  getEntitiesByOwner(playerId: number): Entity[] {
    return this.query(Owner).filter((entity) => {
      const owner = entity.getComponent(Owner);
      return owner && owner.playerId === playerId && !owner.isOnBench;
    });
  }

  /**
   * Get entity count
   */
  get entityCount(): number {
    return this.entities.size;
  }

  /**
   * Get the event bus
   */
  getEventBus(): EventBus {
    return this.eventBus;
  }

  /**
   * Clear all entities and systems
   */
  clear(): void {
    for (const entity of this.entities.values()) {
      entity.destroy();
    }
    this.entities.clear();

    for (const system of this.systems) {
      system.dispose();
    }
    this.systems = [];
  }
}
